import React, { useState } from 'react';
import { ENTITIES, updateEntity, deleteEntity } from '../data/entities';
import { backendBase, isBackendConfigured } from '../utils/backendApi';
import { askNova } from './ConsoleHeader';

// One agent, fully editable: what it says (name, greeting, instructions),
// what it answers from (knowledge binding), what it may touch (registry
// allowlists), how it looks, and how it is installed (script / iframe / API +
// allowed sites). Used by Workflows & Agents (org list) and App Builder →
// Design (the app's attached agents). Safety is in the allowlists, enforced
// server-side — this is only the form.
export const TOOLABLE = ['workspace', 'project', 'pipeline', 'workload'].filter((n) => ENTITIES[n]);

export function randomToken(bytes = 12) {
  const arr = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr).map((b) => b.toString(16).padStart(2, '0')).join('');
}

const HEX = /^#[0-9a-fA-F]{6}$/;

export function agentSnippets(agent) {
  const base = backendBase();
  const token = agent.publicToken;
  return {
    script: `<script src="${base}/agent/embed.js?token=${token}"></script>`,
    iframe: `<iframe src="${base}/agent/frame?token=${token}" title="${(agent.name || 'nventr Agent').replace(/"/g, '')}" style="width:380px;height:560px;border:0"></iframe>`,
    api: `const API = ${JSON.stringify(base)};
const TOKEN = ${JSON.stringify(token)};
async function post(path, body) {
  const res = await fetch(API + path, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(body),
  });
  return res.json();
}
const session = await post('/agent/session', { token: TOKEN });
const turn = await post('/agent/message', { sessionId: session.sessionId, message: 'Hello' });
// await post('/agent/confirm', { sessionId: session.sessionId, proposalId: turn.proposals[0].id, approve: true });`,
  };
}

function StepLabel({ children }) {
  return <h3 style={{ margin: '16px 0 8px', fontSize: 13, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--muted)' }}>{children}</h3>;
}

export function CopyBlock({ label, code }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span className="cs-help" style={{ fontSize: 12 }}>{label}</span>
        <button
          type="button"
          className="cs-btn cs-btn-outline cs-btn-sm"
          onClick={() => {
            navigator.clipboard?.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="cs-card" style={{ padding: 10, marginTop: 6, overflow: 'auto', fontSize: 12 }}>{code}</pre>
    </div>
  );
}

// Install section on its own so App Builder → Surfaces can show it per agent.
export function AgentInstall({ org, agent, canEdit }) {
  const scope = { org: org.id };
  const [origins, setOrigins] = useState((agent.allowedOrigins || []).join('\n'));
  const [state, setState] = useState('');
  const snippets = agentSnippets(agent);
  const saveOrigins = async () => {
    setState('saving');
    try {
      const list = origins.split(/[\n,\s]+/).map((s) => s.trim()).filter(Boolean).slice(0, 50);
      await updateEntity('agent', scope, agent.id, { allowedOrigins: list });
      setState('saved');
    } catch (err) {
      setState(err.message);
    }
  };
  if (agent.enabled === false) return <p className="cs-help">Enable the agent to copy the script, iframe, and API.</p>;
  if (!isBackendConfigured()) return <p className="cs-help">Backend API not configured (REACT_APP_UPLOAD_API).</p>;
  return (
    <>
      <CopyBlock label="Script — paste before </body> on any website" code={snippets.script} />
      <CopyBlock label="Iframe — embed the panel in a page or another app" code={snippets.iframe} />
      <CopyBlock label="API — call it from your own backend or app" code={snippets.api} />
      <StepLabel>Allowed sites</StepLabel>
      <p className="cs-help" style={{ fontSize: 12, margin: '0 0 6px' }}>
        Websites that may load the script embed, one per line (e.g. <code>https://www.example.com</code>). Empty = any site.
        {!(agent.allowedOrigins || []).length && <strong style={{ color: 'var(--warn, #e0a800)' }}> Currently any site can mount this agent.</strong>}
      </p>
      <textarea className="cs-input" style={{ width: '100%', minHeight: 56 }} value={origins} onChange={(e) => setOrigins(e.target.value)} disabled={!canEdit} aria-label="Allowed sites" placeholder="https://www.example.com" />
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
          <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" disabled={state === 'saving'} onClick={saveOrigins}>{state === 'saving' ? 'Saving…' : 'Save sites'}</button>
          {state && state !== 'saving' && <span className={state === 'saved' ? 'cs-help' : 'cs-form-error'}>{state === 'saved' ? 'Saved.' : state}</span>}
        </div>
      )}
    </>
  );
}

// Knowledge binding: everything the org publishes, only this app's documents,
// or documents with given tags. `docs` (optional) = the org's knowledgeDocs
// for the picker; `appDocIds` = the app's linked docs for the one-click bind.
function KnowledgeBinding({ org, agent, canEdit, docs, appDocIds }) {
  const scope = { org: org.id };
  const k = agent.knowledge || {};
  const mode = k.mode === 'docs' || k.mode === 'tags' ? k.mode : 'all';
  const [tags, setTags] = useState((k.tags || []).join(', '));
  const bound = new Set(k.docIds || []);
  const set = (patch) => updateEntity('agent', scope, agent.id, { knowledge: { mode, docIds: k.docIds || [], tags: k.tags || [], ...patch } });
  const toggleDoc = (id) => {
    const next = bound.has(id) ? [...bound].filter((d) => d !== id) : [...bound, id];
    return set({ mode: 'docs', docIds: next });
  };
  const missing = (appDocIds || []).filter((id) => !bound.has(id));
  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[['all', 'Everything this organization publishes'], ['docs', 'Only chosen documents'], ['tags', 'Documents with tags']].map(([m, label]) => (
          <button key={m} type="button" className={`cs-btn cs-btn-sm ${mode === m ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => set({ mode: m })} disabled={!canEdit}>{label}</button>
        ))}
      </div>
      {mode === 'docs' && (
        <div style={{ marginTop: 8 }}>
          {missing.length > 0 && canEdit && (
            <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" style={{ marginBottom: 6 }} onClick={() => set({ mode: 'docs', docIds: [...new Set([...bound, ...appDocIds])] })}>
              Bind all {appDocIds.length} app document{appDocIds.length === 1 ? '' : 's'}
            </button>
          )}
          {docs ? (
            <ul className="mf-list">
              {docs.map((d) => (
                <li key={d.id} className="mf-row">
                  <input type="checkbox" checked={bound.has(d.id)} onChange={() => toggleDoc(d.id)} disabled={!canEdit} aria-label={`Bind ${d.title}`} />
                  <span className="mf-row-grow">{d.title}</span>
                  <span className="cs-help" style={{ fontSize: 12 }}>{d.status}{d.audience === 'internal' ? ' · internal (members only)' : ''}</span>
                </li>
              ))}
              {!docs.length && <li className="cs-help">No documents yet.</li>}
            </ul>
          ) : (
            <span className="cs-help" style={{ fontSize: 12 }}>{bound.size} document{bound.size === 1 ? '' : 's'} bound — pick them in App Builder → Knowledge.</span>
          )}
        </div>
      )}
      {mode === 'tags' && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
          <input className="cs-input" style={{ flex: 1 }} value={tags} onChange={(e) => setTags(e.target.value)} placeholder="hr, benefits" disabled={!canEdit} aria-label="Tags" />
          {canEdit && <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => set({ mode: 'tags', tags: tags.split(',').map((t) => t.trim()).filter(Boolean) })}>Save tags</button>}
        </div>
      )}
      <p className="cs-help" style={{ fontSize: 12, margin: '8px 0 0' }}>
        Visitors on websites and in the hosted app see only documents marked <em>public</em>; signed-in members also get <em>internal</em> ones. Drafts never answer — publish first.
      </p>
    </>
  );
}

export default function AgentEditor({ org, agent, canEdit, docs = null, appDocIds = [], showInstall = true, onDetach = null, defaultOpen = true }) {
  const scope = { org: org.id };
  const theme = agent.theme || {};
  const [open, setOpen] = useState(defaultOpen);
  const [agentName, setAgentName] = useState(agent.name || '');
  const [prompt, setPrompt] = useState(agent.systemPrompt || '');
  const [greeting, setGreeting] = useState(agent.greeting || '');
  const [accent, setAccent] = useState(HEX.test(theme.accent || '') ? theme.accent : '#4f46e5');
  const [side, setSide] = useState(theme.side === 'left' ? 'left' : 'right');
  const [launcher, setLauncher] = useState(theme.launcher || 'Chat');
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState('');

  const toggleList = async (field, entity) => {
    const cur = agent[field] || [];
    const next = cur.includes(entity) ? cur.filter((e) => e !== entity) : [...cur, entity];
    await updateEntity('agent', scope, agent.id, { [field]: next });
  };

  const save = async () => {
    setSaveErr('');
    try {
      await updateEntity('agent', scope, agent.id, {
        ...(agentName.trim() ? { name: agentName.trim() } : {}),
        systemPrompt: prompt,
        greeting,
        theme: { accent: HEX.test(accent) ? accent : '#4f46e5', side: side === 'left' ? 'left' : 'right', launcher: String(launcher || 'Chat').slice(0, 12) },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    } catch (err) {
      setSaveErr(err.message);
    }
  };

  const k = agent.knowledge || {};
  const scopeLabel = k.mode === 'docs' ? `${(k.docIds || []).length} document${(k.docIds || []).length === 1 ? '' : 's'}` : k.mode === 'tags' ? `tags: ${(k.tags || []).join(', ') || '—'}` : 'all published knowledge';

  return (
    <div className="cs-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setOpen((v) => !v)} aria-expanded={open}>{open ? '▾' : '▸'}</button>
        <strong style={{ color: 'var(--text)' }}>{agent.name}</strong>
        <span className="cs-help" style={{ fontSize: 12 }}>{agent.enabled === false ? 'disabled' : 'enabled'} · answers from {scopeLabel}</span>
        <span style={{ flex: 1 }} />
        {canEdit && (
          <>
            <button className="cs-btn cs-btn-outline cs-btn-sm" type="button" onClick={() => updateEntity('agent', scope, agent.id, { enabled: agent.enabled === false })}>
              {agent.enabled === false ? 'Enable' : 'Disable'}
            </button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" type="button" onClick={() => updateEntity('agent', scope, agent.id, { publicToken: randomToken() })} title="Invalidates existing embeds">
              Rotate token
            </button>
            {onDetach ? (
              <button className="cs-btn cs-btn-outline cs-btn-sm" type="button" onClick={onDetach} title="Keeps the agent; removes it from this app">Detach</button>
            ) : (
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                type="button"
                onClick={async () => {
                  // eslint-disable-next-line no-alert
                  if (window.confirm(`Delete agent "${agent.name}"?`)) await deleteEntity('agent', scope, agent.id);
                }}
              >
                Delete
              </button>
            )}
          </>
        )}
      </div>

      {open && canEdit && (
        <>
          <StepLabel>What it says</StepLabel>
          <input className="cs-input" style={{ width: '100%' }} value={agentName} onChange={(e) => setAgentName(e.target.value)} placeholder="Name" aria-label="Agent name" />
          <input className="cs-input" style={{ width: '100%', marginTop: 8 }} value={greeting} onChange={(e) => setGreeting(e.target.value)} placeholder="Greeting — the first thing a visitor sees" aria-label="Greeting" />
          <textarea className="cs-input" style={{ width: '100%', minHeight: 96, marginTop: 8 }} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Instructions — who it is, what it helps with, what it must never do" aria-label="Instructions" />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <button className="cs-btn cs-btn-primary cs-btn-sm" type="button" onClick={save}>{saved ? 'Saved' : 'Save'}</button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" type="button" onClick={() => askNova(`Draft the instructions and greeting for my agent "${agent.name}". It should: ${prompt || '(describe what it helps with)'}. Reply with a greeting line and a short set of instructions I can paste.`)}>◈ Draft with NoVA</button>
            {saveErr && <span className="cs-form-error">{saveErr}</span>}
          </div>

          <StepLabel>What it answers from</StepLabel>
          <KnowledgeBinding org={org} agent={agent} canEdit={canEdit} docs={docs} appDocIds={appDocIds} />

          <StepLabel>What it may touch</StepLabel>
          <span className="cs-help" style={{ fontSize: 12 }}>Records it can look up for a visitor. Off means that tool does not exist for it.</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {TOOLABLE.map((entity) => (
              <button key={entity} type="button" className={`cs-btn cs-btn-sm ${(agent.allowReads || []).includes(entity) ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => toggleList('allowReads', entity)}>
                {entity}
              </button>
            ))}
          </div>
          <span className="cs-help" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>Records it may propose creating. A person confirms before anything exists.</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
            {TOOLABLE.map((entity) => (
              <button key={`w-${entity}`} type="button" className={`cs-btn cs-btn-sm ${(agent.allowWrites || []).includes(entity) ? 'cs-btn-primary' : 'cs-btn-outline'}`} onClick={() => toggleList('allowWrites', entity)}>
                {entity}
              </button>
            ))}
          </div>

          <StepLabel>How it looks</StepLabel>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input aria-label="Accent" type="color" value={accent} onChange={(e) => setAccent(e.target.value)} />
            <select className="cs-input" value={side} onChange={(e) => setSide(e.target.value)} aria-label="Launcher side">
              <option value="right">Right</option>
              <option value="left">Left</option>
            </select>
            <input className="cs-input" style={{ width: 140 }} maxLength={12} value={launcher} onChange={(e) => setLauncher(e.target.value)} placeholder="Launcher label" aria-label="Launcher label" />
            <button className="cs-btn cs-btn-outline cs-btn-sm" type="button" onClick={save}>{saved ? 'Saved' : 'Save look'}</button>
          </div>
        </>
      )}

      {open && showInstall && (
        <>
          <StepLabel>Install</StepLabel>
          <AgentInstall org={org} agent={agent} canEdit={canEdit} />
        </>
      )}
    </div>
  );
}
